import css from './PaymentCheck.module.scss';
import classNames from 'classnames';
import { PaymentCheckProps } from './PaymentCheck.types';
import { useState, useEffect } from 'react';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { AppointmentDispatchTypes, useAppointmentDispatch, useAppointmentState } from '../../../../../context/Appointment/appointment';
import { useArePaymentsEnabled, useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../../context/Localization/localization';
import { LocalizationType, Locales, LocalesFull } from '../../../../../context/Localization/localization.types';
import { GMBox } from '../../../../../controls/Box';
import { CTAButton } from '../../../../../controls/ButtonBase';
import { GMButtonGroup } from '../../../../../controls/ButtonGroup';
import { Mustache } from '../../../../../controls/Heading/Mustache';
import { GMRoundedIcon, GMSizedIcon } from '../../../../../controls/Icon';
import { Spacer } from '../../../../../controls/Spacer/Spacer';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { getFullDisplayName } from '../../../../../helpers/Appointment';
import { useDisplayLocalizedVehicle } from '../../../../../helpers/displayVehicle';
import { formatAsPhoneNumber } from '../../../../../helpers/Number';
import { ScrollableBody } from '../../../../Layout/ScrollableBody/ScrollableBody';
import { KeyManagementHeader } from '../../../../../controls/KeyManagementHeader/KeyManagementHeader';
import DollarCircle from '../../../../../assets/icons/DollarCircle.svg?react';
import { IAppointment } from '../../../../../API/GoMoto';

/**
 * Offers the advisor an option to add payment or skip adding payment.
 *
 * If payment is not enabled for the dealer, when this component is
 * rendered it will automatically be skipped and navigate to the
 * next component.
 *
 * @param onSkip - {@link PaymentCheckProps.onSkip onSkip} description.
 * @param onProceed - {@link PaymentCheckProps.onProceed onProceed} description.
 */
export const PaymentCheck: GoMotoComponent<PaymentCheckProps> = ({
    onSkip,
    onProceed,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { dealer: { primaryLanguage, alternateLanguage } } = useKioskState();
    const paymentsEnabledPromise = useArePaymentsEnabled();
    const appointment: IAppointment = useAppointmentState();
    const appointmentDispatch = useAppointmentDispatch();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();

    const [paymentsEnabled, setPaymentsEnabled] = useState<boolean | undefined>(undefined);
    const [isPending, setIsPending] = useState(false);

    const fullName: string = getFullDisplayName(appointment, false);
    const vehicle: string = displayLocalizedVehicle(appointment)
        .toString();

    useEffect(() => {
        if (paymentsEnabled === undefined) {
            paymentsEnabledPromise()
                .then(setPaymentsEnabled);
        }
        else if (!paymentsEnabled) {
            appointmentDispatch({
                type: AppointmentDispatchTypes.Update,
                payload: { ...appointment,
                    paymentStatus: 'no payment due' },
            });

            onSkip();
        }
    }, [paymentsEnabled, onSkip, paymentsEnabledPromise, appointment, appointmentDispatch]);

    if (!paymentsEnabled) {
        return <Spinner />;
    }
    else {
        return (
            <ScrollableBody
                heading={<KeyManagementHeader secondaryHeading={Localization.IsAPaymentRequiredToPickupKeys} leftAlignSecondaryHeading />}
                proceedButtons={
                    <GMButtonGroup
                        alignment={'center'}
                        buttons={[
                            (
                                <CTAButton
                                    styleType={'primary'}
                                    onClick={() => {
                                        setIsPending(true);
                                        appointmentDispatch({
                                            type: AppointmentDispatchTypes.Update,
                                            payload: { ...appointment,
                                                paymentStatus: 'no payment due' },
                                        });

                                        onProceed(false);
                                    }}
                                    disabled={isPending}
                                    label={Localization.NoPaymentDue}
                                    kioskOptions={{
                                        square: true,
                                        squareOptions: {
                                            roundedIcon: (
                                                <GMRoundedIcon themeType={'primary'} roundedBackgroundSize={'large'}>
                                                    <GMSizedIcon iconType={'No'} iconSize={'custom'} />
                                                </GMRoundedIcon>
                                            ),
                                        },
                                    }}
                                    id={Localization.NoPaymentDue}
                                    ariaLabel={Localization.NoPaymentDue}
                                />
                            ),
                            (
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={() => {
                                        setIsPending(true);
                                        appointmentDispatch({
                                            type: AppointmentDispatchTypes.Update,
                                            payload: { ...appointment,
                                                paymentStatus: 'not paid' },
                                        });

                                        onProceed(true);
                                    }}
                                    disabled={isPending}
                                    label={Localization.PaymentRequired}
                                    kioskOptions={{
                                        squareOptions: {
                                            roundedIcon: <DollarCircle className={css.dollarCircleIcon} />,
                                        },
                                        square: true,
                                    }}
                                    id={Localization.PaymentRequired}
                                    ariaLabel={Localization.PaymentRequired}
                                />
                            ),
                        ]}
                    />
                }
                unsetButtons
                topDivider
                hideSpacer
            >
                <div className={css.customerInformation}>
                    <GMBox color={'primary'}>
                        <div className={classNames(css.label, css.bold)}>{fullName}</div>
                        {appointment.roNumber ? (
                            <div className={classNames(css.label, css.bold)}>{Localization.formatString(Localization.RO, appointment.roNumber)}</div>
                        ) : null}
                        {appointment.pickupEmail ? (
                            <div className={css.label}>{appointment.pickupEmail}</div>
                        ) : null}
                        {appointment.pickupPhone ? (
                            <div className={css.label}>{formatAsPhoneNumber(appointment.pickupPhone)}</div>
                        ) : null}
                        {vehicle.trim() !== '' ? <div className={css.label}>{vehicle}</div> : null}
                        {!!alternateLanguage ? (
                            <div className={css.label}>
                                {Localization.formatString(
                                    Localization.LanguageNotification,
                                    Localization.getString(LocalesFull[appointment.locale !== Locales.none ? appointment.locale : primaryLanguage as Locales]),
                                )}
                            </div>
                        ) : null}
                    </GMBox>
                    <br />
                    <Spacer />
                    <div className={css.mustache}>
                        <Mustache />
                    </div>
                </div>
            </ScrollableBody>
        );
    }
};
